import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useIsSovendusConsentGiven} from '@aboutyou/storage';
import {Promotion, useTrackPromotion} from '@aboutyou/tracking';
import {SkeletonBlock, useInViewEffect} from '@aboutyou/ui-components';
import React from 'react';
import {useSovendusBanner} from './utils/useSovendusBanner';

export interface SovendusBannerProps {
  // order data
  orderId: string;
  orderValue: number;
  orderCurrency: string;
  sessionId: string;
  usedCouponCode?: string;

  // customer data
  salutation?: 'Mr.' | 'Mrs.' | '' | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  countryCode?: string;
  zipCode?: string;
  yearOfBirth?: number;
  streetName?: string;
  streetNumber?: string;
  cityName?: string;

  trafficSourceNumber?: number;
  trafficMediumNumber?: number;
}

export const SovendusBanner: React.FC<SovendusBannerProps> = props => {
  const isConsentBannerAccepted = useIsSovendusConsentGiven();

  if (!isConsentBannerAccepted) {
    return null;
  }

  return <SovendusInnerBanner {...props} />;
};

const SovendusInnerBanner: React.FC<SovendusBannerProps> = props => {
  const trackPromotion = useTrackPromotion();
  const {sovendusScriptState} = useSovendusBanner(props);

  const isLoading = ['idle', 'pending'].includes(sovendusScriptState);

  const trackingPayload = {
    promotion: {
      source: TrackingPromotionCreative.Sovendus,
      promotionId: `${props.trafficSourceNumber}`,
    } as Promotion,
    streamPosition: 0,
  };

  const {ref} = useInViewEffect<HTMLDivElement>(
    inView => {
      if (inView && !isLoading) {
        trackPromotion({
          actionType: 'impression',
          ...trackingPayload,
        });
      }
    },
    {triggerOnce: true, threshold: 0.5},
  );

  const handleClick = () => {
    trackPromotion({
      actionType: 'click',
      ...trackingPayload,
    });
  };

  if (sovendusScriptState === 'error') {
    return null;
  }

  return (
    <>
      {isLoading && (
        <SkeletonBlock data-testid="sovendus-skeleton" $height="200px" />
      )}
      <div
        data-testid="sovendus"
        id={'sovendus-container'}
        ref={ref}
        onClick={handleClick}
      />
    </>
  );
};
